import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { TableSkeleton } from "@/components/skeleton";
import { StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import { useCurrentProject } from "@/features/projects/current-project";
import { formatExpiresAt, isExpiringSoon } from "@/lib/expiry";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { CreateApiKeyDialog } from "./create-dialog";
import { useRevokeApiKey } from "./mutations";
import { type ApiKeySummary, useApiKeys } from "./queries";

export function ApiKeysListPage() {
  const { projectId } = useCurrentProject();
  const { data, isLoading } = useApiKeys(projectId);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const revoke = useRevokeApiKey(projectId ?? "");

  const columns: ColumnDef<ApiKeySummary>[] = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      header: "Prefix",
      cell: ({ row }) => <span className="mono text-fg-muted">{row.original.key_prefix}…</span>,
    },
    {
      header: "RPM",
      cell: ({ row }) => <span className="mono tnum">{row.original.rate_limit_rpm ?? "—"}</span>,
    },
    {
      header: "Expires",
      cell: ({ row }) => {
        const exp = row.original.expires_at;
        const soon = isExpiringSoon(exp);
        return (
          <span
            className={`mono text-xs ${
              soon ? "rounded bg-amber-50 px-1.5 py-0.5 text-amber-900" : "text-fg-muted"
            }`}
            title={exp ?? "Never"}
          >
            {formatExpiresAt(exp)}
          </span>
        );
      },
    },
    {
      header: "Status",
      cell: ({ row }) =>
        row.original.revoked_at ? <StatusDot status="revoked" /> : <StatusDot status="active" />,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const k = row.original;
        if (k.revoked_at) return null;
        return (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                setRevokeTarget(k);
              }}
            >
              Revoke
            </Button>
          </div>
        );
      },
    },
  ];

  if (!projectId)
    return (
      <EmptyState
        title="No project selected"
        description="Choose a project to view its API keys."
      />
    );
  return (
    <>
      <PageHeader
        title="API Keys"
        subtitle="Project-scoped · rate-limited · revocable"
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + Create new key
          </Button>
        }
      />
      {isLoading ? <TableSkeleton /> : <DataTable data={data?.items ?? []} columns={columns} />}
      <CreateApiKeyDialog projectId={projectId} open={createOpen} onOpenChange={setCreateOpen} />
      {revokeTarget && (
        <ConfirmDialog
          open={!!revokeTarget}
          onOpenChange={(o) => {
            if (!o) setRevokeTarget(null);
          }}
          title={`Revoke key "${revokeTarget.name}"`}
          description="Future requests using this key will fail with 401. This cannot be undone."
          confirmLabel="Revoke key"
          tone="danger"
          requireText={revokeTarget.name}
          busy={revoke.isPending}
          onConfirm={async () => {
            await revoke.mutateAsync(revokeTarget.id);
            setRevokeTarget(null);
          }}
        />
      )}
    </>
  );
}
